import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { usePoiSelectedLocations } from '../PointOfInterestExperience/PoiSelectedLocationsProvider';
import { useUpdateNavigationBar } from '../NavigationBar/NavigationBarProvider';
import { useMapData } from '../../map-data/use-map-data';
import { usePoiCountryHierarchy } from '../../map-data/use-poi-hierarchy';
import {
  POI_COUNTRY_LIST_ITEM_ID_PREFIX,
  POI_SUBDIVISION_LIST_ITEM_ID_PREFIX,
} from '../PointOfInterestCountryList/constants';
import { getCountryListScrollContainer } from '../PointOfInterestCountryList/get-list-scroll-container';
import { useFavouritesActions } from '../../providers/FavouritesProvider';
import { Icon } from '../Icon';
import { getRgb } from '../PinMap/get-rgb';
import { CodeBadge } from '../CodeBadge';
import { isEmpty } from '../../utils/string-utils';

import { MapInfoCard } from './MapInfoCard';

type Props = {
  closeCard: (value: boolean) => void;
  cityName: string;
  countryName: string;
  implementation: string;
  studyAcronym?: string;
  studyName?: string;
  studyId: string;
  information: string;
  expand: boolean | null;
  handleExpand: (value: boolean | null) => void;
  region: string | undefined;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  onRegionClick?: (regionName: string) => void;
  onCityClick?: (cityName: string) => void;
  onCountryClick?: (countryName: string) => void;
};

export const ProgrammeCard: FC<Props> = ({
  closeCard,
  cityName,
  countryName,
  implementation,
  studyAcronym,
  studyName,
  studyId,
  information,
  expand,
  region,
  handleExpand,
  onNavigatePrevious,
  onNavigateNext,
  onRegionClick,
  onCityClick,
  onCountryClick,
}) => {
  const { t } = useTranslation();

  const { setShowCountryCard, setShowCityCard, setShowProgramCard } =
    usePoiSelectedLocations();

  const { resetNavigationBar } = useUpdateNavigationBar();
  const { poiCountryData } = useMapData();
  const { countries: hierarchyCountries } = usePoiCountryHierarchy();
  const { togglePoiFavourite, isPoiFavourited } = useFavouritesActions();

  const handleCountryClick = () => {
    // Only change card visibility - don't modify selection state
    setShowCountryCard(true);
    setShowProgramCard(false);
    setShowCityCard(false);
    resetNavigationBar();

    // Call the parent handler to clear selection state
    if (onCountryClick) {
      onCountryClick(countryName);
    }

    // Manually scroll to country
    const scrollContainer = getCountryListScrollContainer();
    if (!scrollContainer || !poiCountryData) return;

    setTimeout(() => {
      const countryIndex = hierarchyCountries.findIndex(
        c => c.name === countryName,
      );
      if (countryIndex === -1) return;

      const listItem = document.querySelector<HTMLLIElement>(
        `#${POI_COUNTRY_LIST_ITEM_ID_PREFIX}-${countryIndex}`,
      );

      if (!listItem) return;

      const listItemRect = listItem.getBoundingClientRect();
      if (listItemRect.height === 0) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const targetScrollTop =
        scrollContainer.scrollTop + (listItemRect.top - containerRect.top);

      scrollContainer.scrollTo({
        behavior: 'smooth',
        top: targetScrollTop,
      });
    }, 50);
  };

  const handleRegionClick = () => {
    if (!region || region === '—') return;

    // Call the callback to trigger map zoom
    if (onRegionClick) {
      onRegionClick(region);
    }

    // Change card visibility
    setShowCityCard(true);
    setShowCountryCard(false);
    setShowProgramCard(false);
    resetNavigationBar();

    // Manually scroll to region, or fall back to city if region not in list
    const scrollContainer = getCountryListScrollContainer();
    if (!scrollContainer) return;

    setTimeout(() => {
      const country = hierarchyCountries.find(c => c.name === countryName);
      if (!country) return;

      // Try to find the region in subdivisions
      let subdivisionIndex = country.subdivisions.findIndex(
        s => s.name === region,
      );

      // If region not found (list is showing cities instead of regions),
      // fall back to scrolling to the city
      if (subdivisionIndex === -1) {
        subdivisionIndex = country.subdivisions.findIndex(
          s => s.name === cityName,
        );
      }

      if (subdivisionIndex === -1) return;

      const listItem = document.querySelector<HTMLLIElement>(
        `#${POI_SUBDIVISION_LIST_ITEM_ID_PREFIX}-${subdivisionIndex}`,
      );

      if (!listItem) return;

      const listItemRect = listItem.getBoundingClientRect();
      if (listItemRect.height === 0) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const targetScrollTop =
        scrollContainer.scrollTop + (listItemRect.top - containerRect.top);

      scrollContainer.scrollTo({
        behavior: 'smooth',
        top: targetScrollTop,
      });
    }, 50);
  };

  const handleCityClick = () => {
    // Call the callback to trigger map zoom
    if (onCityClick) {
      onCityClick(cityName);
    }

    // Change card visibility
    setShowCityCard(true);
    setShowCountryCard(false);
    setShowProgramCard(false);
    resetNavigationBar();

    // Manually scroll to city
    const scrollContainer = getCountryListScrollContainer();
    if (!scrollContainer) return;

    setTimeout(() => {
      const country = hierarchyCountries.find(c => c.name === countryName);
      if (!country) return;

      const subdivisionIndex = country.subdivisions.findIndex(
        s => s.name === cityName,
      );
      if (subdivisionIndex === -1) return;

      const listItem = document.querySelector<HTMLLIElement>(
        `#${POI_SUBDIVISION_LIST_ITEM_ID_PREFIX}-${subdivisionIndex}`,
      );

      if (!listItem) return;

      const listItemRect = listItem.getBoundingClientRect();
      if (listItemRect.height === 0) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const targetScrollTop =
        scrollContainer.scrollTop + (listItemRect.top - containerRect.top);

      scrollContainer.scrollTo({
        behavior: 'smooth',
        top: targetScrollTop,
      });
    }, 50);
  };

  const entryTypeColor = getRgb(implementation, true);

  const favouriteIcon = (
    <div
      role="button"
      tabIndex={0}
      onClick={e => {
        e.stopPropagation();
        togglePoiFavourite(studyId);
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          togglePoiFavourite(studyId);
        }
      }}
      aria-label={
        isPoiFavourited(studyId)
          ? 'Remove from favourites'
          : 'Add to favourites'
      }
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Icon
        variant={isPoiFavourited(studyId) ? 'HeartFilled' : 'Heart'}
        width={18}
        height={18}
        style={{
          color: isPoiFavourited(studyId) ? '#e63946' : '#6a6a6a',
        }}
      />
    </div>
  );

  const headerFields = [
    {
      label: t('card.studyAcronym'),
      value: (
        <>
          <span
            style={{
              transform: 'translateY(-1px)',
              display: 'inline-block',
              marginRight: '0.5em',
            }}
          >
            <CodeBadge code={studyId} backgroundColor={entryTypeColor} />
          </span>
          {studyAcronym && !isEmpty(studyAcronym) && (
            <span
              style={{
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
              }}
            >
              {studyAcronym}
            </span>
          )}
        </>
      ),
      tooltip: isEmpty(studyName)
        ? implementation
        : `${studyName} (${implementation})`,
      icon: favouriteIcon,
    },
    {
      label: t('card.country'),
      value: countryName,
      onClick: handleCountryClick,
    },
    {
      label: t('card.region'),
      value: region ?? '—',
      onClick: region && region !== '—' ? handleRegionClick : undefined,
    },
    {
      label: t('card.city'),
      value: cityName,
      onClick: handleCityClick,
    },
  ];

  return (
    <MapInfoCard
      closeCard={closeCard}
      headerFields={headerFields}
      description={information}
      expand={expand}
      handleExpand={handleExpand}
      onNavigatePrevious={onNavigatePrevious}
      onNavigateNext={onNavigateNext}
    />
  );
};
